import { useState } from 'react';
import { speechLang } from '../../utils/narration';
import { useTranslation } from 'react-i18next';
import { gameAPI } from '../../services/api';
import VoiceInputButton from '../ui/VoiceInputButton';
import { useIsMobile } from '../../hooks/useIsMobile';

interface TurnPanelProps {
  gameId: string;
  playerId: string;
  playerName: string;
  currentPlayer: any;
  isMyTurn: boolean;
  // Co-located scene: several players in one browser describe everyone's actions in one field.
  sceneMode?: boolean;
  presentPlayers?: Array<{ id: string; name: string }>;
}

export default function TurnPanel({ gameId, playerId, currentPlayer, isMyTurn, sceneMode = false, presentPlayers = [] }: TurnPanelProps) {
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();
  const [actionInput, setActionInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);
  const [waitingFor, setWaitingFor] = useState<string[] | null>(null);

  const handleSubmitAction = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!actionInput.trim() || !isMyTurn) return;

    try {
      setIsSubmitting(true);

      // Co-located: one field describes everyone's actions → resolve as one shared scene.
      if (sceneMode && presentPlayers.length > 1) {
        await gameAPI.submitScene(gameId, {
          playerIds: presentPlayers.map((p) => p.id),
          text: actionInput.trim(),
        });
        setWaitingFor(null);
        setActionInput('');
        return; // the resolved scene arrives via the turn:completed socket event
      }

      const result = await gameAPI.submitTurn(gameId, {
        playerId,
        actionType: 'action',
        actionInput: actionInput.trim(),
      });

      // Shared scene: the action was gathered; the scene resolves once everyone acts.
      if (result.status === 'waiting') {
        setWaitingFor(result.waitingFor || []);
        setActionInput('');
        return;
      }

      setWaitingFor(null);
      setLastResult(result.turn);
      setActionInput('');
    } catch (error: any) {
      console.error('Failed to submit turn:', error);
      alert(t('turnPanel.submitError') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      background: 'rgba(15, 12, 41, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '20px',
      border: '1px solid rgba(99, 102, 241, 0.2)',
      padding: isMobile ? '1.25rem' : '2rem',
      marginBottom: '0.5rem',
    }}>
      {/* Current Turn Info */}
      <div style={{
        padding: isMobile ? '1rem' : '1.5rem',
        background: isMyTurn ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.05)',
        borderRadius: '16px',
        border: `1px solid ${isMyTurn ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.2)'}`,
        marginBottom: '2rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
          <span style={{ fontSize: '2rem' }}>
            {isMyTurn ? '🎯' : '⏳'}
          </span>
          <div>
            <h3 style={{ fontSize: isMobile ? '1.15rem' : '1.3rem', color: '#e2e8f0', margin: 0 }}>
              {sceneMode
                ? t('turnPanel.sharedScene')
                : isMyTurn ? t('game.yourTurn') : t('turnPanel.playerTurn', { name: currentPlayer?.name || '...' })}
            </h3>
            {sceneMode ? (
              <p style={{ fontSize: '0.9rem', color: '#a0aec0', margin: '0.25rem 0 0 0' }}>
                {t('turnPanel.togetherOnScene', { names: presentPlayers.map((p) => p.name).join(', ') })}
              </p>
            ) : (!isMyTurn && (
              <p style={{ fontSize: '0.9rem', color: '#a0aec0', margin: '0.25rem 0 0 0' }}>
                {t('turnPanel.waitYourTurn')}
              </p>
            ))}
          </div>
        </div>

        {/* Last Result Preview */}
        {lastResult && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '12px',
            fontSize: '0.9rem',
            color: '#cbd5e0',
          }}>
            <strong style={{ color: '#10b981' }}>{t('turnPanel.lastResult')}</strong>
            <p style={{ margin: '0.5rem 0 0 0', lineHeight: '1.6' }}>
              {lastResult.actionResult?.substring(0, 200)}...
            </p>
          </div>
        )}
      </div>

      {/* Waiting for co-located players to declare their actions */}
      {waitingFor && (
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem 1.25rem',
          background: 'rgba(99, 102, 241, 0.08)',
          border: '1px dashed rgba(99, 102, 241, 0.4)',
          borderRadius: '12px',
          color: '#c7d2fe',
          fontSize: '0.95rem',
        }}>
          ⏳ {t('turnPanel.actionAccepted')} {waitingFor.length > 0
            ? t('turnPanel.waitingInScene', { names: waitingFor.join(', ') })
            : t('turnPanel.gmResolving')}
        </div>
      )}

      {/* Action Input Form */}
      {isMyTurn ? (
        <form onSubmit={handleSubmitAction}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontWeight: '600',
              color: '#e2e8f0',
              fontSize: '1.1rem',
            }}>
              {t('game.action')}
            </label>
            {sceneMode && (
              <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '-0.4rem 0 0.6rem 0' }}>
                {t('turnPanel.sceneHint')}
              </p>
            )}
            <textarea
              value={actionInput}
              aria-label={sceneMode ? t('turnPanel.ariaSceneAction') : t('turnPanel.ariaYourAction')}
              onChange={(e) => setActionInput(e.target.value)}
              placeholder={sceneMode
                ? t('turnPanel.scenePlaceholder')
                : t('turnPanel.actionPlaceholder')}
              rows={4}
              disabled={isSubmitting}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '1rem',
                fontSize: '1rem',
                border: '2px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                color: '#fff',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.6',
                transition: 'all 0.3s',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                e.target.style.boxShadow = '0 0 20px rgba(99, 102, 241, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: isMobile ? '0.5rem' : '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <VoiceInputButton
              onTranscript={(text) =>
                setActionInput((prev) => (prev.trim() ? prev.trim() + ' ' : '') + text)
              }
              language={speechLang(i18n.language)}
              disabled={isSubmitting}
            />
            <button
              type="submit"
              aria-label={t('turnPanel.ariaSubmit')}
              disabled={isSubmitting || !actionInput.trim()}
              style={{
                flex: 1,
                padding: '1.25rem',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '12px',
                background: (isSubmitting || !actionInput.trim())
                  ? 'rgba(99, 102, 241, 0.3)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                cursor: (isSubmitting || !actionInput.trim()) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 10px 25px rgba(99, 102, 241, 0.3)',
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting && actionInput.trim()) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(99, 102, 241, 0.5)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(99, 102, 241, 0.3)';
              }}
            >
              {isSubmitting ? `⏳ ${t('game.processing')}` : `🎮 ${t('game.submitAction')}`}
            </button>
          </div>
        </form>
      ) : (
        <div style={{
          padding: isMobile ? '2rem 1rem' : '3rem 2rem',
          textAlign: 'center',
          background: 'rgba(99, 102, 241, 0.05)',
          borderRadius: '16px',
          border: '1px dashed rgba(99, 102, 241, 0.3)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏸️</div>
          <p style={{ color: '#a0aec0', fontSize: '1.1rem' }}>
            {t('turnPanel.waitingForPlayer')} <strong style={{ color: '#cbd5e0' }}>{currentPlayer?.name}</strong>
          </p>
        </div>
      )}
    </div>
  );
}
